import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import {
  Button,
  Checkbox,
  ComposedModal,
  Dropdown,
  InlineNotification,
  Loading,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@carbon/react';
import {
  CheckmarkFilled,
  CloseFilled,
  Rocket,
  Undefined,
} from '@carbon/react/icons';
import classNames from 'classnames';
import { format0 } from 'utils/number';
import { toastAtom } from 'pages/type-curves/atoms';
import type {
  InventoryWorkspaceResponse,
  InventoryWorkspacePromotionPlanResponse,
  InventoryWorkspaceTask,
  InventoryTaskStatus,
} from 'types/api-responses';
import type { PromoteInventoryRow } from 'types/api-payloads';
import {
  inventoryPromotionPlanAtom,
  promoteInventoryWorkspaceAtom,
} from './atoms';
import {
  isUnknownZone,
  resolveTreePackages,
  type TreeZonePackage,
} from './tree-leaves';
import style from './promote-workspace-modal.module.scss';

interface PromoteWorkspaceModalProps {
  open: boolean;
  workspace: InventoryWorkspaceResponse;
  /** The launched type curve's label (tc_public_id), for the impact line. */
  typeCurveLabel: string | null;
  /** Workspace-level gate verdict, shown as "Your verification". */
  gateTask?: InventoryWorkspaceTask | null;
  /** The checklist, gate excluded — the "All tasks" list. */
  tasks?: InventoryWorkspaceTask[];
  /**
   * Scenario the promote publishes into, named in the title per the v14 mock.
   * The show payload does not carry it yet, so the title falls back rather than
   * naming a scenario we cannot actually resolve.
   */
  scenarioName?: string | null;
  onClose: () => void;
  /** Promote succeeded — the caller refetches the (now promoted) workspace. */
  onPromoted?: () => void;
}

/**
 * NOT_PROMOTABLE hangs the outstanding verification tasks off details.incomplete,
 * one entry per required task still missing a verdict —
 * `{ path, name, reason: 'UNVERIFIED' }`, not a string list.
 */
/** Long enough to collapse a burst of checkbox ticks into one request. */
const PLAN_DEBOUNCE_MS = 300;

interface IncompleteTask {
  path: string;
  name: string;
  reason: string;
}

interface PromoteErrorBody {
  errors?: { message?: string; details?: { incomplete?: IncompleteTask[] } }[];
  error?: string;
}

/** Sentinel option that swaps the zone's dropdown for a name field. */
const CREATE_NEW_INVENTORY = '＋ Create new inventory…';

/** What the operator has chosen to publish for one zone. */
interface ZoneTarget {
  /** Selected dropdown option — a package label, or CREATE_NEW_INVENTORY. */
  inventory: string;
  /** Typed name, used only while `inventory` is the create sentinel. */
  newName: string;
  setAsDefault: boolean;
}

const count = (value: number | null | undefined) => {
  return value === null || value === undefined ? 0 : value;
};

/**
 * A zone package's dropdown label. Spacing rides in the label rather than its
 * own column, and the family default is marked, both per the v14 mock. Takes the
 * bare package identity so it serves both a tree package and a plan-derived one.
 * @param {object} pkg - The package identity (name, wellSpacingFt, isDefault).
 * @return {string} The option label.
 */
const packageLabel = (pkg: {
  name: string | null;
  wellSpacingFt: number | null;
  isDefault: boolean;
}): string => {
  const name = pkg.name ?? 'New package';
  const spacing =
    pkg.wellSpacingFt === null ? null : `${format0(pkg.wellSpacingFt)} ft`;
  const base = spacing ? `${name} — ${spacing}` : name;
  return pkg.isDefault ? `${base} (default)` : base;
};

/**
 * The name to publish a zone as: the typed one while creating, else the zone's
 * authoritative current package name — the one the promotion plan resolved
 * (`published_name_for`), NOT the tree's default-else-first package.
 * @param {ZoneTarget} target - The zone's current selection.
 * @param {string | null} resolvedName - The plan-resolved package name for the zone.
 * @return {string | null} The name, or null to let the server keep its own.
 */
const publishedName = (
  target: ZoneTarget,
  resolvedName: string | null
): string | null => {
  if (target.inventory === CREATE_NEW_INVENTORY) {
    return target.newName.trim() || null;
  }
  return resolvedName;
};

/**
 * Up / down colouring on an After value, so a zone that gains or loses reads at
 * a glance. Equal values stay unstyled.
 * @param {number} after - After count.
 * @param {number} before - Before count.
 * @return {string | undefined} The delta class.
 */
const deltaClass = (after: number, before: number): string | undefined => {
  if (after > before) return style.up;
  if (after < before) return style.down;
  return undefined;
};

/**
 * The mock's rule: the default flag follows the chosen inventory's label.
 * @param {string} label - The selected option's label.
 * @return {boolean} Whether it names the family default.
 */
const inventoryIsDefault = (label: string) =>
  label.toLowerCase().includes('default');

/**
 * A verification item's verdict as the mock words it.
 * @param {boolean | null} state - correctnessState.
 * @return {string} The label.
 */
const verdictText = (state: InventoryTaskStatus): string => {
  if (state === 'correct') return 'Correct';
  if (state === 'incorrect') return 'Incorrect';
  return 'Unverified';
};

/**
 * Verdict icon, matching the mock's checkmark / close / undefined trio.
 * @param {boolean | null} state - correctnessState.
 * @return {JSX.Element} The icon.
 */
const VerdictIcon = ({ state }: { state: InventoryTaskStatus }) => {
  if (state === 'correct') return <CheckmarkFilled size={16} />;
  if (state === 'incorrect') return <CloseFilled size={16} />;
  return <Undefined size={16} />;
};

/**
 * Promote confirmation for an Inventory Workspace (v14).
 *
 * Each zone chooses what it publishes as and whether it takes its `(area,
 * formation)` default — both are per-zone on the server
 * (`inventories[].inventory_package_name` / `set_as_default`), and the v14 mock
 * exposes them per row rather than as one workspace-wide toggle.
 *
 * The mock keys its rows by formation; this keys by zone id, because a
 * workspace here spans several Type Curve Areas and the same formation recurs
 * across them — formation alone would collide.
 *
 * @param {PromoteWorkspaceModalProps} props - Open state, workspace and callbacks.
 * @return {JSX.Element} The modal.
 */
export const PromoteWorkspaceModal = ({
  open,
  workspace,
  typeCurveLabel,
  scenarioName,
  gateTask = null,
  tasks = [],
  onClose,
  onPromoted,
}: PromoteWorkspaceModalProps) => {
  const setToast = useSetAtom(toastAtom);
  const [{ loading: planLoading, result: planResult }, fetchPlan] = useAtom(
    inventoryPromotionPlanAtom
  );
  const [{ loading: promoting }, promoteWorkspace] = useAtom(
    promoteInventoryWorkspaceAtom
  );

  const [error, setError] = useState<string | null>(null);
  const [incomplete, setIncomplete] = useState<IncompleteTask[]>([]);
  /**
   * True until the first plan lands. Only that first load gates Promote:
   * afterwards the operator is editing config, and a refetch disabling the
   * button under them made it flicker on every checkbox.
   */
  const [awaitingFirstPlan, setAwaitingFirstPlan] = useState(true);

  const zones = useMemo(
    () => resolveTreePackages(workspace.typeCurveTree),
    [workspace.typeCurveTree]
  );

  // Verdict and tasks come from the page's checklist query, not a fetch of our
  // own: the tasks panel writes through that same React Query cache, so a
  // verdict saved there is reflected here instead of going stale.
  const workspaceVerdict = gateTask?.status ?? null;
  const tasksAnswered = tasks.filter((task) => task.status !== null).length;

  // One selection per zone. Seeded from the PROMOTION PLAN, not the tree: the
  // plan's `inventoryPackageName` is the zone's authoritative current package
  // (`published_name_for`), whereas the tree hands back its default-else-first
  // package, which is what showed a stale value here.
  const [targets, setTargets] = useState<Record<number, ZoneTarget>>({});
  // The authoritative package NAME per zone, captured from the FIRST plan (which
  // is fetched with an empty selection, so its names are resolved by
  // `published_name_for` rather than an echo of ours). Only the name is taken
  // from the plan — spacing and the family-default flag stay the tree's, which
  // is what the row's spacing label and the default checkbox read. Held stable so
  // the option labels don't drift as later, selection-scoped plans echo back
  // whatever the operator typed.
  const [planNames, setPlanNames] = useState<Record<number, string | null>>({});
  // Seed the selections once per open, on the first plan. Reset on close so a
  // reopen re-seeds from a fresh plan.
  const seededRef = useRef(false);

  const updateTarget = (zoneId: number, patch: Partial<ZoneTarget>) => {
    setTargets((prev) => ({
      ...prev,
      [zoneId]: { ...prev[zoneId], ...patch },
    }));
  };

  // The plan's authoritative spacing per zone (`well_spacing_ft`, the mutable
  // current spacing — NOT `source_well_spacing_ft`, which anchors the plan's
  // `before` at the spawn spacing). The plan is fetched fresh on open, so it is
  // fresher than the page's tree if the zone was re-spaced after the page loaded.
  const planSpacingByZone = useMemo(() => {
    const entries =
      planResult?.type === 'success' ? planResult.data.perInventory : [];
    const map = new Map<number, number | null>();
    entries.forEach((entry) => {
      if (entry.inventoryFormationPackageId !== null) {
        map.set(entry.inventoryFormationPackageId, entry.wellSpacingFt);
      }
    });
    return map;
  }, [planResult]);

  // A zone's package identity for the dropdown: the plan's authoritative name and
  // spacing (each falling back to the tree only before the first plan lands), with
  // the default flag from the tree.
  const zoneInfoFor = useCallback(
    (zone: TreeZonePackage) => ({
      name: planNames[zone.inventoryFormationPackageId] ?? zone.name,
      wellSpacingFt:
        planSpacingByZone.get(zone.inventoryFormationPackageId) ??
        zone.wellSpacingFt,
      isDefault: zone.isDefault,
    }),
    [planNames, planSpacingByZone]
  );

  // A zone with no package name has nothing to publish under, so block Promote
  // until every zone names its inventory — its own name, or a typed new one. The
  // UNKNOWN bucket is exempt: it offers no selection and is promoted as-is.
  const hasMissingPackageName = useMemo(
    () =>
      zones.some((zone) => {
        if (isUnknownZone(zone.formation)) return false;
        const target = targets[zone.inventoryFormationPackageId];
        const resolved = zoneInfoFor(zone).name;
        const name = target ? publishedName(target, resolved) : resolved;
        return name === null || name.trim() === '';
      }),
    [zones, targets, zoneInfoFor]
  );

  const plan: InventoryWorkspacePromotionPlanResponse | null =
    planResult?.type === 'success' ? planResult.data : null;

  /**
   * The per-zone rows both calls send. Built from the current selections, so
   * the preview reflects exactly what a promote would publish.
   * @param {Record<number, ZoneTarget>} state - Current selections.
   * @return {PromoteInventoryRow[]} One row per zone.
   */
  const rowsFor = useCallback(
    (state: Record<number, ZoneTarget>): PromoteInventoryRow[] =>
      zones.map((zone) => {
        const target = state[zone.inventoryFormationPackageId];
        const info = zoneInfoFor(zone);
        return {
          inventoryFormationPackageId: zone.inventoryFormationPackageId,
          formation: zone.formation,
          inventoryPackageName: target
            ? publishedName(target, info.name)
            : info.name,
          wellSpacingFt: info.wellSpacingFt,
          setAsDefault: target ? target.setAsDefault : info.isDefault,
        };
      }),
    [zones, zoneInfoFor]
  );

  const loadPlan = useCallback(
    (state: Record<number, ZoneTarget>) => {
      fetchPlan({
        url: `/inventory_workspaces/${workspace.id}/promotion_plan`,
        type: 'POST',
        data: { workspace: { inventories: rowsFor(state) } },
      });
    },
    [fetchPlan, workspace.id, rowsFor]
  );

  // The FIRST plan is fetched with an EMPTY selection. `published_name_for`
  // echoes any name we send and only returns the zone's authoritative current
  // name when the zone has no row in the body — so an empty body is the only way
  // to read the real current package rather than a round-trip of our own guess.
  const loadEmptyPlan = useCallback(() => {
    fetchPlan({
      url: `/inventory_workspaces/${workspace.id}/promotion_plan`,
      type: 'POST',
      data: { workspace: { inventories: [] } },
    });
  }, [fetchPlan, workspace.id]);

  // Fire the authoritative first plan on open, before any selection exists.
  useEffect(() => {
    if (!open) return;
    loadEmptyPlan();
  }, [open, loadEmptyPlan]);

  // Seed the dropdowns from that first plan (once). The NAME and spacing are
  // taken from the plan (its `well_spacing_ft` is the authoritative current
  // spacing); only the default flag comes off the tree zone, since the plan does
  // not carry it. Spacing falls back to the tree before/if the plan omits it.
  useEffect(() => {
    if (!open || !plan || seededRef.current) return;
    seededRef.current = true;
    const treeById = new Map(
      zones.map((zone) => [zone.inventoryFormationPackageId, zone])
    );
    const nextNames: Record<number, string | null> = {};
    const nextTargets: Record<number, ZoneTarget> = {};
    plan.perInventory.forEach((entry) => {
      const id = entry.inventoryFormationPackageId;
      if (id === null) return;
      const treePkg = treeById.get(id);
      const isDefault = treePkg?.isDefault ?? false;
      const resolvedName = entry.inventoryPackageName ?? treePkg?.name ?? null;
      nextNames[id] = entry.inventoryPackageName;
      nextTargets[id] = {
        // A zone with no promoted inventory opens straight in "Create new"
        // mode (name field shown) rather than a dead "New package" placeholder,
        // so the operator can just type a name to make it promotable.
        inventory: resolvedName
          ? packageLabel({
              name: resolvedName,
              wellSpacingFt:
                entry.wellSpacingFt ?? treePkg?.wellSpacingFt ?? null,
              isDefault,
            })
          : CREATE_NEW_INVENTORY,
        newName: '',
        setAsDefault: isDefault,
      };
    });
    setPlanNames(nextNames);
    setTargets(nextTargets);
  }, [open, plan, zones]);

  // Config edits after the first plan refetch a selection-scoped preview. Keyed
  // on a digest rather than the object so typing a new name does not refetch.
  //
  // Debounced, because the operator edits config in bursts: ticking ten zones
  // fired ten POSTs, each recomputing a resolver-backed diff per unit, and
  // nothing cancelled the ones already in flight — so a slower earlier response
  // could land last and render a plan for a selection already moved past.
  const planKey = Object.entries(targets)
    .map(([id, t]) => `${id}:${t.inventory}:${t.setAsDefault}`)
    .join('|');
  useEffect(() => {
    if (!open || awaitingFirstPlan || planKey === '') return;
    const timer = setTimeout(() => loadPlan(targets), PLAN_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planKey]);

  // The first plan to land releases the button; later ones are the preview
  // catching up and leave it alone.
  useEffect(() => {
    if (planResult) setAwaitingFirstPlan(false);
  }, [planResult]);

  // Reopening starts the gate again — the modal is remounted with a fresh plan,
  // and the seed re-arms so the dropdowns re-seed from it.
  useEffect(() => {
    if (!open) {
      setAwaitingFirstPlan(true);
      seededRef.current = false;
    }
  }, [open]);

  const handlePromote = () => {
    if (promoting) return;
    setError(null);
    setIncomplete([]);

    promoteWorkspace({
      url: `/inventory_workspaces/${workspace.id}`,
      type: 'PATCH',
      data: { workspace: { promote: true, inventories: rowsFor(targets) } },
      onSuccess: (data) => {
        const published = data.promotedInventories?.length ?? 0;
        setToast({
          kind: 'success',
          title: 'Inventory Workspace promoted',
          subtitle: `${workspace.name} — ${published} inventor${
            published === 1 ? 'y' : 'ies'
          } published.`,
        });
        onPromoted?.();
        onClose();
      },
      // NOT_PROMOTABLE lists the unverified tasks; anything else has no
      // reasons to show, so surface whatever message the API gave.
      onError: (err?: PromoteErrorBody) => {
        const first = err?.errors?.[0];
        const blocking = first?.details?.incomplete ?? [];
        setIncomplete(blocking);
        setError(
          blocking.length
            ? `Unable to promote: ${blocking.length} verification task(s) are not verified.`
            : first?.message ||
                err?.error ||
                'Unable to promote. Please try again.'
        );
      },
    });
  };

  const planByZone = useMemo(
    () =>
      new Map(
        (plan?.perInventory ?? []).map((target) => [
          target.inventoryFormationPackageId,
          target,
        ])
      ),
    [plan]
  );

  // Column totals for the "All formations" row: summed over every status row of
  // every zone, so it reconciles with the lines above it.
  const totals = (plan?.perInventory ?? [])
    .flatMap((target) => target.rows)
    .reduce(
      (acc, row) => ({
        valuedBefore: acc.valuedBefore + count(row.before.valued),
        totalBefore: acc.totalBefore + count(row.before.total),
        valuedAfter: acc.valuedAfter + count(row.after.valued),
        totalAfter: acc.totalAfter + count(row.after.total),
      }),
      { valuedBefore: 0, totalBefore: 0, valuedAfter: 0, totalAfter: 0 }
    );

  // One line per status row whose counts move — the mock's summary list.
  const changeSummary = (plan?.perInventory ?? [])
    .filter((target) => target.changed)
    .flatMap((target) =>
      target.rows
        .filter(
          (row) =>
            row.before.valued !== row.after.valued ||
            row.before.total !== row.after.total
        )
        .map(
          (row) =>
            `${target.formation ?? '--'} · ${row.status}: ${count(
              row.before.valued
            )}/${count(row.before.total)} → ${count(row.after.valued)}/${count(
              row.after.total
            )}`
        )
    );

  // Every zone the operator has ticked, listed one by one as the mock does.
  // Ticking is the trigger on its own: the mock does not wait for the plan to
  // name an incumbent, and requiring one hid the banner entirely here, since
  // `currentDefaultWorkspaceId` comes back null until a zone has a live default.
  const defaultingZones = zones.filter(
    (zone) => targets[zone.inventoryFormationPackageId]?.setAsDefault
  );
  const droppedUnitCount = defaultingZones.reduce(
    (total, zone) =>
      total +
      (planByZone.get(zone.inventoryFormationPackageId)?.unitsDroppedFromDefault
        .length ?? 0),
    0
  );

  return (
    <ComposedModal
      open={open}
      onClose={onClose}
      preventCloseOnClickOutside
      size="lg"
      className={style.modal}
    >
      <ModalHeader
        label={workspace.name}
        title={
          scenarioName
            ? `Promote to Type Curve Scenario: ${scenarioName}`
            : 'Promote Inventory Workspace'
        }
      />
      <ModalBody aria-label="Promote Inventory Workspace">
        <div className={style.body}>
          <p className={style.message}>
            Promoting the Workspace <strong>{workspace.name}</strong> will
            result in the Inventory for Type Curve{' '}
            <strong>{typeCurveLabel ?? workspace.uniqueTcId}</strong> to be
            recorded and exposed for economic evaluation.
          </p>

          {error && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              className={style.defaultNotification}
              title={error}
              subtitle={
                incomplete.length
                  ? incomplete.map((task) => task.name).join(' · ')
                  : undefined
              }
            />
          )}

          <div className={style.sectionLabel}>
            Target inventory{zones.length > 0 ? ` (${zones.length})` : ''}
          </div>

          {defaultingZones.length > 0 && (
            <InlineNotification
              kind="info"
              lowContrast
              hideCloseButton
              className={style.defaultNotification}
              title="Setting a default replaces the current default"
              subtitle={`${defaultingZones
                .map((zone) => {
                  const target = targets[zone.inventoryFormationPackageId];
                  return `${zone.formation} → ${
                    target ? target.inventory : packageLabel(zoneInfoFor(zone))
                  }`;
                })
                .join(' · ')}${
                droppedUnitCount > 0
                  ? ` — ${droppedUnitCount} unit(s) leave the current default`
                  : ''
              }`}
            />
          )}

          {planLoading && !plan ? (
            <Loading withOverlay={false} small />
          ) : zones.length === 0 ? (
            <p className={style.emptyPlan}>
              This workspace has no zones to promote.
            </p>
          ) : (
            <table className={style.planTable}>
              <thead>
                <tr>
                  <th>Formation</th>
                  <th>Inventory</th>
                  <th>Set as default</th>
                  <th>Status</th>
                  <th>Before (valued/total)</th>
                  <th>After (valued/total)</th>
                </tr>
              </thead>
              <tbody>
                {zones.map((zone) => {
                  const zoneId = zone.inventoryFormationPackageId;
                  const target = targets[zoneId];
                  const planned = planByZone.get(zoneId);
                  // A zone whose statuses resolve to nothing still gets a line:
                  // it is being promoted, and dropping it made the table read as
                  // if the zone were not part of the promote at all.
                  const rows = planned?.rows.length
                    ? planned.rows
                    : [
                        {
                          status: '—',
                          before: { valued: 0, total: 0 },
                          after: { valued: 0, total: 0 },
                        },
                      ];
                  // The UNKNOWN bucket is not a real formation: it offers no
                  // inventory selection (and does not gate Promote), so its cell
                  // is a static placeholder rather than a dropdown.
                  const isUnknown = isUnknownZone(zone.formation);
                  // A zone with no promoted inventory offers only "Create new"
                  // (no misleading "New package" placeholder to pick).
                  const resolved = zoneInfoFor(zone).name;
                  const hasInventory = !!resolved && resolved.trim() !== '';
                  const options = hasInventory
                    ? [packageLabel(zoneInfoFor(zone)), CREATE_NEW_INVENTORY]
                    : [CREATE_NEW_INVENTORY];

                  return rows.map((row, rowIndex) => (
                    <tr key={`${zoneId}-${row.status}`}>
                      {rowIndex === 0 && (
                        <>
                          <td rowSpan={rows.length}>{zone.formation}</td>
                          <td rowSpan={rows.length} className={style.zoneCell}>
                            {isUnknown ? (
                              <span className={style.noInventory}>—</span>
                            ) : (
                              <>
                                <Dropdown
                                  id={`promote-inventory-${zoneId}`}
                                  titleText={`Inventory — ${zone.formation}`}
                                  hideLabel
                                  // Floats the menu: in a scrolling modal body an
                                  // in-flow menu grows its row and clips at the
                                  // container edge.
                                  autoAlign
                                  label="Select inventory"
                                  size="sm"
                                  items={options}
                                  selectedItem={target?.inventory ?? options[0]}
                                  onChange={({
                                    selectedItem,
                                  }: {
                                    selectedItem: string | null;
                                  }) => {
                                    const picked = selectedItem ?? options[0];
                                    // Mock rule: picking an inventory re-derives
                                    // the default flag, so "Create new" clears it.
                                    updateTarget(zoneId, {
                                      inventory: picked,
                                      setAsDefault: inventoryIsDefault(picked),
                                    });
                                  }}
                                />
                                {target?.inventory === CREATE_NEW_INVENTORY && (
                                  <TextInput
                                    id={`promote-new-inventory-${zoneId}`}
                                    labelText={`New inventory name — ${zone.formation}`}
                                    hideLabel
                                    placeholder="New inventory name"
                                    size="sm"
                                    value={target.newName}
                                    // Blocks Promote until named (hasMissingPackageName)
                                    // — surface that per row instead of a silently
                                    // dead button.
                                    invalid={target.newName.trim() === ''}
                                    invalidText="Enter an inventory name to promote this formation"
                                    onChange={(
                                      event: React.ChangeEvent<HTMLInputElement>
                                    ) =>
                                      updateTarget(zoneId, {
                                        newName: event.target.value,
                                      })
                                    }
                                  />
                                )}
                              </>
                            )}
                          </td>
                          <td rowSpan={rows.length} className={style.zoneCell}>
                            <Checkbox
                              id={`promote-default-${zoneId}`}
                              labelText={`Set ${zone.formation} as default`}
                              hideLabel
                              checked={target?.setAsDefault ?? zone.isDefault}
                              onChange={(
                                _event: unknown,
                                { checked }: { checked: boolean }
                              ) =>
                                updateTarget(zoneId, {
                                  setAsDefault: checked,
                                })
                              }
                            />
                          </td>
                        </>
                      )}
                      <td className={style.statusCell}>{row.status}</td>
                      <td>
                        {count(row.before.valued)}/{count(row.before.total)}
                      </td>
                      <td>
                        <span
                          className={classNames(
                            deltaClass(
                              count(row.after.valued),
                              count(row.before.valued)
                            )
                          )}
                        >
                          {count(row.after.valued)}
                        </span>
                        /
                        <span
                          className={classNames(
                            deltaClass(
                              count(row.after.total),
                              count(row.before.total)
                            )
                          )}
                        >
                          {count(row.after.total)}
                        </span>
                      </td>
                    </tr>
                  ));
                })}
                {plan && (
                  <tr className={style.totalsRow}>
                    <td colSpan={4}>
                      <strong>All formations</strong>
                    </td>
                    <td>
                      <strong>
                        {totals.valuedBefore}/{totals.totalBefore}
                      </strong>
                    </td>
                    <td>
                      <strong>
                        <span
                          className={classNames(
                            deltaClass(totals.valuedAfter, totals.valuedBefore)
                          )}
                        >
                          {totals.valuedAfter}
                        </span>
                        /
                        <span
                          className={classNames(
                            deltaClass(totals.totalAfter, totals.totalBefore)
                          )}
                        >
                          {totals.totalAfter}
                        </span>
                      </strong>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          <div className={style.outcomeRow}>
            <span className={style.sectionLabel}>Your verification</span>
            <span
              className={classNames(style.outcomeValue, {
                [style.outcomeCorrect]: workspaceVerdict === 'correct',
                [style.outcomeIncorrect]: workspaceVerdict === 'incorrect',
                [style.outcomeIdle]: workspaceVerdict === null,
              })}
            >
              <VerdictIcon state={workspaceVerdict} />
              {workspaceVerdict === null
                ? 'Not marked'
                : verdictText(workspaceVerdict)}
            </span>
          </div>

          <div className={style.changeSummary}>
            <span className={style.sectionLabel}>Summary of changes</span>
            <ul className={style.summaryList}>
              {changeSummary.length === 0 ? (
                <li className={style.summaryDetail}>No changes</li>
              ) : (
                changeSummary.map((line) => (
                  <li key={line} className={style.summaryDetail}>
                    {line}
                  </li>
                ))
              )}
            </ul>
          </div>

          {tasks.length > 0 && (
            <div className={style.taskSummary}>
              <span className={style.sectionLabel}>
                All tasks ({tasksAnswered}/{tasks.length})
              </span>
              <ul className={style.taskList}>
                {tasks.map((task) => (
                  <li key={task.id} className={style.taskItem}>
                    <span className={style.taskIcon}>
                      <VerdictIcon state={task.status} />
                    </span>
                    <span className={style.taskName}>{task.title}</span>
                    <span className={style.taskStatus}>
                      {verdictText(task.status)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose} disabled={promoting}>
          Cancel
        </Button>
        <Button
          kind="primary"
          renderIcon={Rocket}
          onClick={handlePromote}
          disabled={
            promoting ||
            awaitingFirstPlan ||
            zones.length === 0 ||
            hasMissingPackageName
          }
        >
          {promoting ? 'Promoting...' : 'Promote'}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};
